import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./DeepInsightsSourcePicker.css";

import autoIcon from "../../assets/new-images/deep-insights-auto-icon.svg";
import sourcesIcon from "../../assets/new-images/deep-insights-sources-icon.svg";
import dropdownIcon from "../../assets/new-images/dropdown-icon.svg";
import checkIcon from "../../assets/images/check-icon.svg";
import { useConstants } from "../../constant/constantProvider";

const DeepInsightsSourcePicker = ({
  sources = [],
  selectedSourceIds = [],
  onChange,
  onManageSources,
  disabled = false,
  isLoading = false,
}) => {
  const { deepInsights } = useConstants();
  const {
    SOURCE_PICKER_ALL_SOURCES,
    SOURCE_PICKER_ARIA_LABEL,
    SOURCE_PICKER_AUTO,
    SOURCE_PICKER_AUTO_DESCRIPTION,
    SOURCE_PICKER_SELECT_LABEL,
    SOURCE_PICKER_LOADING,
    SOURCE_PICKER_EMPTY,
    SOURCE_PICKER_MANAGE,
    SOURCE_PICKER_SELECTED_COUNT,
  } = deepInsights;
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  const isAuto = selectedSourceIds.length === 0;

  const updateMenuPosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const width = Math.min(360, window.innerWidth - 24);
    const left = Math.min(
      Math.max(12, rect.left),
      window.innerWidth - width - 12
    );

    setMenuPosition({
      bottom: window.innerHeight - rect.top + 8,
      left,
      width,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    updateMenuPosition();
    const handlePointerDown = (event) => {
      if (
        !triggerRef.current?.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  const setAuto = () => onChange?.([]);

  const toggleSource = (sourceId) => {
    const nextSelectedIds = selectedSourceIds.includes(sourceId)
      ? selectedSourceIds.filter((id) => id !== sourceId)
      : [...selectedSourceIds, sourceId];
    onChange?.(nextSelectedIds);
  };

  const selectedSources = sources.filter((source) =>
    selectedSourceIds.includes(source.id)
  );
  const triggerLabel =
    selectedSources.length === 0
      ? SOURCE_PICKER_ALL_SOURCES
      : selectedSources.length === 1
        ? selectedSources[0].name
        : `${selectedSources[0].name} +${selectedSources.length - 1}`;
  return (
    <div className="deep-insights-source-picker">
      <button
        ref={triggerRef}
        type="button"
        className={`deep-insights-source-picker-trigger ${isOpen ? "is-open" : ""} ${!isAuto ? "has-selection" : ""}`}
        onClick={() => setIsOpen((open) => !open)}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-controls={menuId}
        aria-haspopup="dialog"
      >
        <span className="deep-insights-source-picker-trigger-icon">
          <img src={sourcesIcon} alt="" />
        </span>
        <span className="deep-insights-source-picker-trigger-label">{triggerLabel}</span>
        <span className="deep-insights-source-picker-chevron">
          <img src={dropdownIcon} alt="" />
        </span>
      </button>

      {isOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            className="deep-insights-source-picker-menu"
            style={menuPosition}
            role="dialog"
            aria-label={SOURCE_PICKER_ARIA_LABEL}
          >
            <button
              type="button"
              className="deep-insights-source-picker-auto"
              onClick={setAuto}
            >
              <span className="deep-insights-source-picker-auto-icon">
                <img src={autoIcon} alt="" />
              </span>
              <span className="deep-insights-source-picker-auto-copy">
                <span className="deep-insights-source-picker-source-title">
                  {SOURCE_PICKER_AUTO}
                </span>
                <span className="deep-insights-source-picker-auto-description">
                  {SOURCE_PICKER_AUTO_DESCRIPTION.replace("{count}", sources.length)}
                </span>
              </span>
              <span className={`deep-insights-source-picker-radio ${isAuto ? "is-selected" : ""}`} />
            </button>

            <div className="deep-insights-source-picker-divider" />
            <div className="deep-insights-source-picker-section-label">
              {SOURCE_PICKER_SELECT_LABEL}
            </div>

            {isLoading ? (
              <div className="deep-insights-source-picker-empty">{SOURCE_PICKER_LOADING}</div>
            ) : sources.length === 0 ? (
              <div className="deep-insights-source-picker-empty">{SOURCE_PICKER_EMPTY}</div>
            ) : (
              <div className="deep-insights-source-picker-list">
                {sources.map((source) => {
                  const isSelected = selectedSourceIds.includes(source.id);
                  return (
                    <label className="deep-insights-source-picker-source" key={source.id}>
                      <span className="deep-insights-source-picker-source-icon">
                        {source.icon_base64 && <img src={source.icon_base64} alt="" />}
                      </span>
                      <span className="deep-insights-source-picker-source-title">
                        {source.name}
                      </span>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSource(source.id)}
                      />
                      <span className={`deep-insights-source-picker-checkbox ${isSelected ? "is-selected" : ""}`}>
                        {isSelected && <img src={checkIcon} alt="" />}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            <div className="deep-insights-source-picker-footer">
              <button
                type="button"
                className="deep-insights-source-picker-manage"
                onClick={() => {
                  setIsOpen(false);
                  onManageSources?.();
                }}
              >
                {SOURCE_PICKER_MANAGE} <span aria-hidden="true">→</span>
              </button>
              <span>
                {isLoading
                  ? ""
                  : sources.length === 0
                    ? ""
                    : isAuto
                      ? SOURCE_PICKER_AUTO
                      : SOURCE_PICKER_SELECTED_COUNT.replace(
                        "{count}",
                        selectedSourceIds.length
                      )}
              </span>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DeepInsightsSourcePicker;
